import type { Identifier } from "ra-core";
import type { AlertRuleType } from "../types";

/**
 * Almikia Epic ER (vision #12, TASK-008): the Direccion alerts section's own
 * pure grouping layer -- `DireccionAlertsSection.tsx` never sorts, groups or
 * counts anything itself, it only renders what this file returns. Deliberately
 * LEAD-shaped only: this type carries no advisor/owner field at all, since the
 * section (ASSUMPTION, ticket description) never groups, counts, sorts or
 * filters by advisor -- the lead page's own "Asignado a" + reassign action is
 * where an owner's name belongs.
 *
 * See adr/ADR-ee2f59a9-TASK-008-direccion-alerts-section.md.
 */
export interface DireccionAlertRow {
  id: Identifier;
  dealId: Identifier;
  /**
   * The deal's first `contact_ids` entry, or `null` when it has none. Mobile
   * registers no `deals` resource (`MobileAdmin`, `CRM.tsx`), so the section's
   * row link falls back to this lead's contact show there, matching
   * `LeadAlertCards.alertCardHref` / `HoyPersonCard.cardHref`.
   */
  contactId: Identifier | null;
  ruleId: Identifier;
  ruleType: AlertRuleType;
  ruleLabel: string;
  stageKey: string;
  stageLabel: string;
  leadName: string;
  message: string;
  /** `lead_alerts.raised_at` -- the alert's own creation time. */
  raisedAt: string;
  /**
   * `raisedAt` minus the rule's own threshold (hours, `resolveThresholdHours`)
   * converted to milliseconds -- how "stale" the alert really is, not merely
   * when the `lead_alerts` row was created. A `sin_seguimiento` alert raised
   * yesterday against a 6-hour threshold is older, in this sense, than a
   * `detenido_en_etapa` alert raised a week ago against a 30-day threshold.
   * Used for the oldest-first ordering below (acceptance criterion 3).
   */
  staleSinceMs: number;
}

export interface RuleAlertGroup {
  ruleId: Identifier;
  ruleType: AlertRuleType;
  ruleLabel: string;
  /** Oldest first (acceptance criterion 3: "ordered by alert age, oldest first"). */
  alerts: DireccionAlertRow[];
}

export interface StuckStageSummary {
  stageKey: string;
  stageLabel: string;
  count: number;
  /** `count >= threshold` -- the rule's own `stuck_lead_count_threshold` parameter. */
  isHighlighted: boolean;
}

function byStaleSinceAscending(
  a: DireccionAlertRow,
  b: DireccionAlertRow,
): number {
  return a.staleSinceMs - b.staleSinceMs;
}

/**
 * Groups open alerts by rule, each group's own rows ordered oldest-first by
 * `staleSinceMs` (acceptance criterion 3 -- alert AGE, not raw
 * `lead_alerts.raised_at`, so a short-threshold rule's alert can rank older
 * than a long-threshold rule's alert raised earlier). Groups themselves are
 * ordered by their stalest alert first too (the group holding the single
 * stalest alert leads), a stable tiebreak on `ruleLabel` when two groups'
 * stalest alert ties -- never on advisor identity, which this row type
 * doesn't even carry.
 */
export function groupAlertsByRule(rows: DireccionAlertRow[]): RuleAlertGroup[] {
  const byRule = new Map<Identifier, RuleAlertGroup>();
  for (const row of rows) {
    let group = byRule.get(row.ruleId);
    if (!group) {
      group = {
        ruleId: row.ruleId,
        ruleType: row.ruleType,
        ruleLabel: row.ruleLabel,
        alerts: [],
      };
      byRule.set(row.ruleId, group);
    }
    group.alerts.push(row);
  }

  const groups = Array.from(byRule.values());
  for (const group of groups) {
    group.alerts.sort(byStaleSinceAscending);
  }
  groups.sort((a, b) => {
    const oldestA = a.alerts[0]?.staleSinceMs ?? 0;
    const oldestB = b.alerts[0]?.staleSinceMs ?? 0;
    const diff = oldestA - oldestB;
    return diff !== 0 ? diff : a.ruleLabel.localeCompare(b.ruleLabel);
  });
  return groups;
}

const DETENIDO_EN_ETAPA: AlertRuleType = "detenido_en_etapa";

/**
 * Almikia Epic ER (vision #12, "N detenidos en etapa"): a per-stage headcount
 * of the currently open `detenido_en_etapa` alerts, sorted by count
 * descending (the worst-stuck stage leads), stable tiebreak on `stageLabel`.
 * `threshold` is the rule's own `stuck_lead_count_threshold`
 * (`alert_rules`, TASK-003) -- a stage reaching it is `isHighlighted`.
 */
export function groupStuckByStage(
  rows: DireccionAlertRow[],
  threshold: number,
): StuckStageSummary[] {
  const byStage = new Map<string, { stageLabel: string; count: number }>();
  for (const row of rows) {
    if (row.ruleType !== DETENIDO_EN_ETAPA) continue;
    const existing = byStage.get(row.stageKey);
    if (existing) {
      existing.count += 1;
    } else {
      byStage.set(row.stageKey, { stageLabel: row.stageLabel, count: 1 });
    }
  }

  return Array.from(byStage.entries())
    .map(([stageKey, { stageLabel, count }]) => ({
      stageKey,
      stageLabel,
      count,
      isHighlighted: count >= threshold,
    }))
    .sort(
      (a, b) => b.count - a.count || a.stageLabel.localeCompare(b.stageLabel),
    );
}
